import React, { useState } from 'react';
import { 
  Brain, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Eye,
  Download,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Bell,
  Calendar,
  User,
  Building,
  Tag,
  BarChart3,
  Activity,
  Zap,
  Shield,
  Lock,
  Users,
  Settings,
  ExternalLink,
  Archive,
  Copy,
  Send,
  AlertCircle,
  Info,
  CheckSquare,
  Square,
  Target,
  Flag,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Bookmark,
  Share,
  MoreHorizontal,
  DollarSign,
  PieChart,
  MapPin,
  Phone,
  Mail,
  Smartphone,
  Monitor,
  Tablet,
  Globe,
  Key,
  Fingerprint,
  Smartphone as Mobile,
  Lightbulb,
  Cpu,
  Database,
  Network,
  Layers,
  GitBranch,
  GitCommit,
  GitMerge,
  GitPullRequest,
  GitCompare,
  GitBranch as GitBranchIcon,
  GitCommit as GitCommitIcon,
  GitMerge as GitMergeIcon,
  GitPullRequest as GitPullRequestIcon,
  GitCompare as GitCompareIcon,
  GitBranch as GitBranchIcon2,
  GitCommit as GitCommitIcon2,
  GitMerge as GitMergeIcon2,
  GitPullRequest as GitPullRequestIcon2,
  GitCompare as GitCompareIcon2,
  Trash2
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AIInsights = () => {
  const [activeTab, setActiveTab] = useState('insights');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Mock data for AI insights
  const [insightsData, setInsightsData] = useState([
    {
      id: 'AI-001',
      insightName: 'Contract Risk Analysis - Q1 2024',
      insightType: 'Risk Analysis',
      category: 'Contract Risk',
      status: 'Active',
      priority: 'High',
      confidence: 94,
      accuracy: 92,
      generatedDate: '2024-02-15T10:30:00Z',
      lastUpdated: '2024-02-16T14:20:00Z',
      nextUpdate: '2024-03-15T10:30:00Z',
      dataSource: 'Contract Database',
      algorithm: 'Risk Assessment ML',
      modelVersion: 'v2.1',
      responsiblePerson: 'AI Analyst',
      responsibleEmail: 'ai.analyst@company.com',
      department: 'AI & Analytics',
      riskLevel: 'Medium',
      impact: 'High',
      recommendations: [
        'Review high-risk contracts immediately',
        'Implement additional monitoring for vendor agreements',
        'Consider renegotiating terms for contracts with high risk scores'
      ],
      keyFindings: [
        '15% of contracts show elevated risk indicators',
        'Vendor contracts have 23% higher risk scores',
        'Payment terms are the primary risk factor'
      ],
      isActive: true,
      tags: ['risk-analysis', 'contracts', 'ml'],
      notes: 'Critical risk analysis showing elevated contract risks requiring immediate attention',
      attachments: ['risk_analysis_report.pdf', 'contract_risk_data.xlsx'],
      stakeholders: ['Legal Team', 'Procurement', 'Risk Management'],
      actionItems: 3,
      completedActions: 1
    },
    {
      id: 'AI-002',
      insightName: 'Asset Performance Prediction',
      insightType: 'Predictive Analytics',
      category: 'Asset Management',
      status: 'Active',
      priority: 'Medium',
      confidence: 87,
      accuracy: 89,
      generatedDate: '2024-02-14T09:15:00Z',
      lastUpdated: '2024-02-15T16:45:00Z',
      nextUpdate: '2024-02-28T09:15:00Z',
      dataSource: 'Asset Management System',
      algorithm: 'Time Series Forecasting',
      modelVersion: 'v1.8',
      responsiblePerson: 'Asset Analyst',
      responsibleEmail: 'asset.analyst@company.com',
      department: 'Asset Management',
      riskLevel: 'Low',
      impact: 'Medium',
      recommendations: [
        'Schedule maintenance for 12 assets showing performance decline',
        'Consider replacement for 3 assets with critical performance issues',
        'Optimize maintenance schedules based on predictive insights'
      ],
      keyFindings: [
        '12 assets predicted to require maintenance within 30 days',
        '3 assets showing critical performance degradation',
        'Maintenance costs could be reduced by 15% with optimized scheduling'
      ],
      isActive: true,
      tags: ['predictive', 'assets', 'maintenance'],
      notes: 'Predictive analytics showing optimal maintenance scheduling opportunities',
      attachments: ['asset_performance_report.pdf', 'maintenance_schedule.xlsx'],
      stakeholders: ['Maintenance Team', 'Operations', 'Finance'],
      actionItems: 5,
      completedActions: 2
    },
    {
      id: 'AI-003',
      insightName: 'Anomaly Detection - Financial Transactions',
      insightType: 'Anomaly Detection',
      category: 'Financial Monitoring',
      status: 'Alert',
      priority: 'High',
      confidence: 96,
      accuracy: 94,
      generatedDate: '2024-02-16T08:45:00Z',
      lastUpdated: '2024-02-16T12:30:00Z',
      nextUpdate: '2024-02-23T08:45:00Z',
      dataSource: 'Financial Systems',
      algorithm: 'Isolation Forest',
      modelVersion: 'v3.0',
      responsiblePerson: 'Financial Analyst',
      responsibleEmail: 'financial.analyst@company.com',
      department: 'Finance',
      riskLevel: 'High',
      impact: 'High',
      recommendations: [
        'Investigate 5 anomalous transactions immediately',
        'Review vendor payment patterns for irregularities',
        'Implement additional fraud detection measures'
      ],
      keyFindings: [
        '5 transactions flagged as anomalous',
        'Unusual payment patterns detected in vendor transactions',
        'Potential fraud indicators identified'
      ],
      isActive: true,
      tags: ['anomaly', 'financial', 'fraud-detection'],
      notes: 'Critical anomaly detection alert - immediate investigation required',
      attachments: ['anomaly_report.pdf', 'transaction_analysis.xlsx'],
      stakeholders: ['Finance Team', 'Risk Management', 'Internal Audit'],
      actionItems: 4,
      completedActions: 0
    },
    {
      id: 'AI-004',
      insightName: 'Contract Renewal Optimization',
      insightType: 'Optimization',
      category: 'Contract Management',
      status: 'Active',
      priority: 'Medium',
      confidence: 91,
      accuracy: 88,
      generatedDate: '2024-02-13T11:20:00Z',
      lastUpdated: '2024-02-14T15:10:00Z',
      nextUpdate: '2024-03-13T11:20:00Z',
      dataSource: 'Contract Management System',
      algorithm: 'Optimization Engine',
      modelVersion: 'v2.3',
      responsiblePerson: 'Contract Manager',
      responsibleEmail: 'contract.manager@company.com',
      department: 'Contract Management',
      riskLevel: 'Low',
      impact: 'Medium',
      recommendations: [
        'Renew 8 contracts early to secure better terms',
        'Negotiate 12 contracts for improved pricing',
        'Consider alternative vendors for 3 contracts'
      ],
      keyFindings: [
        '8 contracts eligible for early renewal with better terms',
        '12 contracts have negotiation opportunities',
        'Potential savings of $2.3M through optimization'
      ],
      isActive: true,
      tags: ['optimization', 'contracts', 'renewal'],
      notes: 'Contract renewal optimization showing significant savings opportunities',
      attachments: ['renewal_optimization_report.pdf', 'savings_analysis.xlsx'],
      stakeholders: ['Procurement', 'Legal Team', 'Finance'],
      actionItems: 6,
      completedActions: 3
    },
    {
      id: 'AI-005',
      insightName: 'Compliance Monitoring Dashboard',
      insightType: 'Monitoring',
      category: 'Compliance',
      status: 'Active',
      priority: 'High',
      confidence: 93,
      accuracy: 91,
      generatedDate: '2024-02-12T14:00:00Z',
      lastUpdated: '2024-02-13T09:30:00Z',
      nextUpdate: '2024-02-26T14:00:00Z',
      dataSource: 'Compliance Systems',
      algorithm: 'Compliance Monitoring ML',
      modelVersion: 'v1.9',
      responsiblePerson: 'Compliance Officer',
      responsibleEmail: 'compliance@company.com',
      department: 'Compliance',
      riskLevel: 'Medium',
      impact: 'High',
      recommendations: [
        'Address 3 compliance violations immediately',
        'Update 7 policies to meet new regulations',
        'Schedule compliance training for 15 employees'
      ],
      keyFindings: [
        '3 compliance violations require immediate attention',
        '7 policies need updates for regulatory compliance',
        '15 employees require compliance training'
      ],
      isActive: true,
      tags: ['compliance', 'monitoring', 'regulatory'],
      notes: 'Compliance monitoring showing violations requiring immediate action',
      attachments: ['compliance_report.pdf', 'violation_details.xlsx'],
      stakeholders: ['Legal Team', 'HR', 'Risk Management'],
      actionItems: 8,
      completedActions: 2
    },
    {
      id: 'AI-006',
      insightName: 'Cost Optimization Analysis',
      insightType: 'Cost Analysis',
      category: 'Financial Optimization',
      status: 'Completed',
      priority: 'Medium',
      confidence: 89,
      accuracy: 86,
      generatedDate: '2024-02-10T16:30:00Z',
      lastUpdated: '2024-02-11T10:15:00Z',
      nextUpdate: '2024-03-10T16:30:00Z',
      dataSource: 'Financial Systems',
      algorithm: 'Cost Optimization ML',
      modelVersion: 'v2.0',
      responsiblePerson: 'Cost Analyst',
      responsibleEmail: 'cost.analyst@company.com',
      department: 'Finance',
      riskLevel: 'Low',
      impact: 'Medium',
      recommendations: [
        'Implement cost reduction measures in 5 departments',
        'Optimize vendor contracts for 20% savings',
        'Reduce operational costs by 12% through process improvements'
      ],
      keyFindings: [
        'Potential savings of $1.8M identified',
        '5 departments have optimization opportunities',
        'Vendor contract optimization could save 20%'
      ],
      isActive: false,
      tags: ['cost-optimization', 'financial', 'savings'],
      notes: 'Cost optimization analysis completed - implementation in progress',
      attachments: ['cost_optimization_report.pdf', 'savings_plan.xlsx'],
      stakeholders: ['Finance', 'Operations', 'Procurement'],
      actionItems: 7,
      completedActions: 7
    }
  ]);

  // Form state
  const [formData, setFormData] = useState({
    insightName: '',
    insightType: 'Risk Analysis',
    category: 'Contract Risk',
    priority: 'Medium',
    dataSource: '',
    algorithm: '',
    responsiblePerson: '',
    responsibleEmail: '',
    department: '',
    notes: ''
  });

  // Calculate statistics
  const stats = {
    totalInsights: insightsData.length,
    activeInsights: insightsData.filter(i => i.status === 'Active').length,
    alertInsights: insightsData.filter(i => i.status === 'Alert').length,
    completedInsights: insightsData.filter(i => i.status === 'Completed').length,
    highPriority: insightsData.filter(i => i.priority === 'High').length,
    averageConfidence: insightsData.reduce((sum, i) => sum + i.confidence, 0) / insightsData.length,
    averageAccuracy: insightsData.reduce((sum, i) => sum + i.accuracy, 0) / insightsData.length,
    totalActionItems: insightsData.reduce((sum, i) => sum + i.actionItems, 0),
    completedActionItems: insightsData.reduce((sum, i) => sum + i.completedActions, 0)
  };

  // Filtered data
  const filteredInsights = insightsData.filter(insight => {
    const matchesSearch = searchTerm === '' || 
      insight.insightName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.insightType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || insight.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || insight.priority === filterPriority;
    const matchesType = filterType === 'all' || insight.insightType === filterType;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesType;
  });

  const tabs = [
    { id: 'insights', label: 'AI Insights' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'completed', label: 'Completed' },
    { id: 'all', label: 'All Insights' },
    { id: 'analytics', label: 'Analytics' }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'id',
      label: 'Insight ID',
      width: '100px'
    },
    {
      key: 'insightName',
      label: 'Insight',
      width: '300px',
      render: (value, row) => (
        <div>
          <div className="fw-medium">{value}</div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {row.insightType} • {row.category}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => {
        const statusConfig = {
          'Active': { className: 'badge-success', icon: <CheckCircle size={14} /> },
          'Alert': { className: 'badge-danger', icon: <AlertTriangle size={14} /> },
          'Completed': { className: 'badge-secondary', icon: <CheckSquare size={14} /> }
        };
        const config = statusConfig[status] || statusConfig['Active'];
        
        return (
          <span className={`badge ${config.className} d-flex align-items-center gap-1`}>
            {config.icon}
            {status}
          </span>
        );
      }
    },
    {
      key: 'confidence',
      label: 'Confidence',
      width: '120px',
      render: (confidence) => {
        const getConfidenceColor = (confidence) => {
          if (confidence >= 90) return '#10b981';
          if (confidence >= 70) return '#f59e0b';
          return '#ef4444';
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${confidence}%`,
                height: '100%',
                backgroundColor: getConfidenceColor(confidence),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: getConfidenceColor(confidence) }}>
              {confidence}%
            </span>
          </div>
        );
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      width: '100px',
      render: (priority) => {
        const priorityConfig = {
          'High': { className: 'badge-danger' },
          'Medium': { className: 'badge-warning' },
          'Low': { className: 'badge-success' }
        };
        const config = priorityConfig[priority] || priorityConfig['Medium'];
        
        return (
          <span className={`badge ${config.className}`}>
            {priority}
          </span>
        );
      }
    },
    {
      key: 'actionItems',
      label: 'Action Items',
      width: '120px',
      render: (actionItems, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Target size={14} style={{ color: 'var(--primary-color)' }} />
          <span style={{ fontWeight: '500', color: '#1f2937' }}>
            {row.completedActions}/{actionItems}
          </span>
        </div>
      )
    },
    {
      key: 'generatedDate',
      label: 'Generated',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  // Handler functions
  const handleView = (insight) => {
    setSelectedInsight(insight);
    setShowViewModal(true);
  };

  const handleDownload = async (insight) => {
    setIsLoading(true);
    try {
      //console.log('Downloading report for:', insight.insightName);
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (insight) => {
    setSelectedInsight(insight);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    setFormData({
      insightName: '',
      insightType: 'Risk Analysis',
      category: 'Contract Risk',
      priority: 'Medium',
      dataSource: '',
      algorithm: '',
      responsiblePerson: '',
      responsibleEmail: '',
      department: '',
      notes: ''
    });
    setShowAddModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      setShowAddModal(false);
      setSelectedInsight(null);
    } catch (error) {
      console.error('Error creating insight:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      setInsightsData(prev => prev.filter(insight => insight.id !== selectedInsight.id));
      setShowDeleteModal(false);
      setSelectedInsight(null);
    } catch (error) {
      console.error('Error deleting insight:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="AI Insights & Analytics"
        description="AI-powered insights, risk analysis, anomaly detection, and predictive analytics"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map(tab => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.id === 'insights' && <Brain size={18} className="page-tab-icon" />}
                {tab.id === 'alerts' && <AlertTriangle size={18} className="page-tab-icon" />}
                {tab.id === 'completed' && <CheckCircle size={18} className="page-tab-icon" />}
                {tab.id === 'all' && <BarChart3 size={18} className="page-tab-icon" />}
                {tab.id === 'analytics' && <PieChart size={18} className="page-tab-icon" />}
                {tab.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="page-stats-grid">
        <div className="page-kpi-card color-1">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.totalInsights}</div>
                <div className="page-kpi-label">Total Insights</div>
              </div>
              <div className="page-kpi-icon-container">
                <Brain size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-2">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.activeInsights}</div>
                <div className="page-kpi-label">Active Insights</div>
              </div>
              <div className="page-kpi-icon-container">
                <CheckCircle size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-3">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.alertInsights}</div>
                <div className="page-kpi-label">Alert Insights</div>
              </div>
              <div className="page-kpi-icon-container">
                <AlertTriangle size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-4">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{Math.round(stats.averageConfidence)}%</div>
                <div className="page-kpi-label">Avg. Confidence</div>
              </div>
              <div className="page-kpi-icon-container">
                <TrendingUp size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Avg. Accuracy</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{Math.round(stats.averageAccuracy)}%</h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#10b98120',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#10b981'
          }}>
            <Target size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Action Items</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {stats.completedActionItems}/{stats.totalActionItems}
            </h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#f59e0b20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#f59e0b'
          }}>
            <CheckSquare size={20} />
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      {(activeTab === 'insights' || activeTab === 'alerts' || activeTab === 'completed' || activeTab === 'all') && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Search Insights
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={18} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  placeholder="Search by insight name, type, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>
            </div>
            
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Status Filter
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Alert">Alert</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Priority Filter
              </label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All Priorities</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Type Filter
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All Types</option>
                <option value="Risk Analysis">Risk Analysis</option>
                <option value="Predictive Analytics">Predictive Analytics</option>
                <option value="Anomaly Detection">Anomaly Detection</option>
                <option value="Optimization">Optimization</option>
                <option value="Monitoring">Monitoring</option>
                <option value="Cost Analysis">Cost Analysis</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleAddNew}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Plus size={16} />
                New Insight
              </button>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                  setFilterPriority('all');
                  setFilterType('all');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <RefreshCw size={16} />
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - DataTable */}
      {(activeTab === 'insights' || activeTab === 'alerts' || activeTab === 'completed' || activeTab === 'all') && (
        <div className="page-tab-content">
          <div className="page-table-container">
            <DataTable
              data={filteredInsights}
              columns={columns}
              title="AI Insights & Analytics"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={true}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleDownload}
              onDelete={handleDelete}
              searchPlaceholder="Search insights by name, type, or status..."
              emptyMessage="No AI insights found. Click 'New Insight' to get started."
              initialHiddenColumns={['id']}
            />
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          AI Analytics Dashboard Coming Soon!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete AI Insight"
        message={`Are you sure you want to delete insight "${selectedInsight?.insightName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      </div>
    </>
  );
};

export default AIInsights;
